import React, { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { ArrowLeft } from "lucide-react";
import {
  getAllConversationsPortal,
  getConversationMessagesPortal,
  getAvailableUsersPortal,
  createConversationPortal,
  addGroupMemberPortal,
  removeGroupMemberPortal,
  uploadChatFilePortal,
  deleteMessagePortal,
} from "../../api/chat/chatPortalApi";
import { useStaffAuth } from "../../context/StaffAuthContext";
import ChatSidebar from "../../components/chat/ChatSidebar";
import MessageList from "../../components/chat/MessageList";
import MessageInput from "../../components/chat/MessageInput";
import NewChatModal from "../../components/chat/NewChatModal";
import GroupInfoModal from "../../components/chat/GroupInfoModal";

const SOCKET_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const ChatPortalPage = ({ currentStaffId: currentStaffIdProp }) => {
  const { staff } = useStaffAuth();
  const currentStaffId =
    currentStaffIdProp || staff?._id || staff?.id || null;

  console.log("[ChatPortalPage] AUTH DEBUG", {
    currentStaffIdProp,
    staff,
    staff_id: staff?._id,
    staff_id_field: staff?.id,
    resolvedCurrentStaffId: currentStaffId,
    resolvedType: typeof currentStaffId,
  });

  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [onlineUserIds, setOnlineUserIds] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [typingUsers, setTypingUsers] = useState([]);

  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  const [isGroupInfoModalOpen, setIsGroupInfoModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const socketRef = useRef(null);
  const currentStaffIdRef = useRef(currentStaffId);
  useEffect(() => {
    currentStaffIdRef.current = currentStaffId;
  }, [currentStaffId]);

  const activeConversationRef = useRef(null);
  useEffect(() => {
    activeConversationRef.current = activeConversation;
    setTypingUsers([]);
    if (activeConversation?._id) {
      const key = String(activeConversation._id);
      setUnreadCounts((prev) => {
        if (!prev[key] && !prev[activeConversation._id]) return prev;
        const next = { ...prev };
        delete next[key];
        delete next[activeConversation._id];
        return next;
      });
    }
  }, [activeConversation]);

  useEffect(() => {
    console.log("[ChatPortalPage] mount → fetchConversations + fetchUsers");
    fetchConversations();
    fetchUsers();
  }, []);

  useEffect(() => {
    socketRef.current = io(SOCKET_URL, {
      withCredentials: true,
      transports: ["polling", "websocket"],
      auth: { userType: "staff" },
      query: { userType: "staff" },
    });

    socketRef.current.on("connect", () => {
      console.log("Portal Staff connected to Socket Server as staff");
    });

    socketRef.current.on("new_message", ({ conversationId, message }) => {
      const activeId = activeConversationRef.current?._id;

      console.log("[ChatPortalPage] socket new_message", {
        conversationId,
        activeId,
        currentStaffId: currentStaffIdRef.current,
        message: {
          _id: message?._id,
          senderId: message?.senderId,
          senderIdType: typeof message?.senderId,
          senderId_id: message?.senderId?._id,
          content: message?.content?.slice?.(0, 40),
          type: message?.type,
        },
      });

      const convKey = String(conversationId);
      const senderKey = String(
        message?.rawSenderId ||
          message?.senderId?._id ||
          message?.senderId ||
          ""
      );

      if (activeId && convKey === String(activeId)) {
        setMessages((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          if (list.some((m) => String(m._id) === String(message._id))) return list;
          return [...list, message];
        });
        if (senderKey) {
          setTypingUsers((prev) =>
            prev.filter((id) => String(id) !== senderKey)
          );
        }
      } else if (
        senderKey &&
        senderKey !== String(currentStaffIdRef.current || "")
      ) {
        // Optimistic local bump — fetchConversations(false) below will shortly
        // overwrite this with the authoritative server-computed unreadCount.
        setUnreadCounts((prev) => ({
          ...prev,
          [convKey]: (prev[convKey] || prev[conversationId] || 0) + 1,
        }));
      }

      fetchConversations(false);
    });

    socketRef.current.on("message_deleted", ({ messageId, conversationId }) => {
      const activeId = activeConversationRef.current?._id;
      if (!activeId || String(conversationId) === String(activeId) || !conversationId) {
        setMessages((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          return list.filter((m) => String(m._id) !== String(messageId));
        });
      }
    });

    socketRef.current.on("typing_start", ({ conversationId, userId: typingUserId }) => {
      const activeId = activeConversationRef.current?._id;
      if (!activeId || String(conversationId) !== String(activeId)) return;
      if (String(typingUserId) === String(currentStaffIdRef.current)) return;
      setTypingUsers((prev) =>
        prev.includes(typingUserId) ? prev : [...prev, typingUserId]
      );
    });

    socketRef.current.on("typing_stop", ({ conversationId, userId: typingUserId }) => {
      const activeId = activeConversationRef.current?._id;
      if (!activeId || String(conversationId) !== String(activeId)) return;
      setTypingUsers((prev) => prev.filter((id) => String(id) !== String(typingUserId)));
    });

    socketRef.current.on(
      "message_reaction",
      ({ conversationId, messageId, reactions }) => {
        const activeId = activeConversationRef.current?._id;
        if (!activeId || String(conversationId) !== String(activeId)) return;
        setMessages((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          return list.map((m) =>
            String(m._id) === String(messageId)
              ? { ...m, reactions: reactions || [] }
              : m
          );
        });
      }
    );

    socketRef.current.on("user_online", ({ userId }) => {
      setOnlineUserIds((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return [...new Set([...list, userId])];
      });
    });

    socketRef.current.on("user_offline", ({ userId }) => {
      setOnlineUserIds((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.filter((id) => id !== userId);
      });
    });

    socketRef.current.on("error", (err) => {
      console.error("Chat socket error:", err?.message || err);
    });

    return () => {
      socketRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (activeConversation?._id) {
      socketRef.current?.emit("join_room", {
        conversationId: activeConversation._id,
      });
      fetchMessages(activeConversation._id);
    }

    return () => {
      if (activeConversation?._id) {
        socketRef.current?.emit("leave_room", {
          conversationId: activeConversation._id,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversation?._id]);

  const fetchConversations = async (showLoading = true) => {
    if (showLoading) setLoadingConversations(true);
    try {
      const res = await getAllConversationsPortal();
      const list = Array.isArray(res) ? res : res?.data || [];
      setConversations(list);

      // Seed/refresh unread counts from the server, which is the source of
      // truth (computed from each participant's lastReadAt). This keeps
      // badges correct across page refreshes and reconnects, not just for
      // messages received while the socket happened to be connected.
      setUnreadCounts((prev) => {
        const next = { ...prev };
        list.forEach((c) => {
          if (typeof c.unreadCount === "number") {
            next[String(c._id)] = c.unreadCount;
          }
        });
        return next;
      });
    } catch (err) {
      console.error("Failed to fetch portal conversations:", err);
      setConversations([]);
    } finally {
      if (showLoading) setLoadingConversations(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await getAvailableUsersPortal();
      const list = Array.isArray(res) ? res : res?.data || [];
      setAvailableUsers(list);
    } catch (err) {
      console.error("Failed to fetch staff directory:", err);
      setAvailableUsers([]);
    }
  };

  const fetchMessages = async (conversationId) => {
    setLoadingMessages(true);
    try {
      const res = await getConversationMessagesPortal(conversationId);
      console.log("[ChatPortalPage] fetchMessages RAW response", res);
      const list = Array.isArray(res) ? res : res?.data?.messages || [];
      const chronological = [...list].reverse();
      console.log(
        "[ChatPortalPage] fetchMessages parsed list",
        JSON.stringify(
          {
            count: chronological.length,
            currentStaffId: currentStaffId?.toString?.() || currentStaffId,
            sample: chronological.slice(0, 3).map((m) => ({
              _id: m._id,
              rawSenderId: m.rawSenderId ?? null,
              senderIdType: typeof m.senderId,
              senderId_id: m.senderId?._id ?? null,
              content: (m.content || "").slice(0, 40),
              type: m.type,
            })),
          },
          null,
          2
        )
      );
      setMessages(chronological);
    } catch (err) {
      console.error("[ChatPortalPage] Failed to fetch messages:", err);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = (messagePayload) => {
    if (!activeConversation) return;

    console.log("[ChatPortalPage] handleSendMessage", {
      conversationId: activeConversation._id,
      currentStaffId,
      payload: messagePayload,
    });

    socketRef.current?.emit("typing_stop", {
      conversationId: activeConversation._id,
    });

    socketRef.current?.emit("send_message", {
      conversationId: activeConversation._id,
      ...messagePayload,
    });
  };

  const handleTypingStart = () => {
    if (!activeConversation?._id) return;
    socketRef.current?.emit("typing_start", {
      conversationId: activeConversation._id,
    });
  };

  const handleTypingStop = () => {
    if (!activeConversation?._id) return;
    socketRef.current?.emit("typing_stop", {
      conversationId: activeConversation._id,
    });
  };

  const handleToggleReaction = (messageId, emoji) => {
    if (!activeConversation?._id || !messageId || !emoji) return;
    socketRef.current?.emit("toggle_reaction", {
      conversationId: activeConversation._id,
      messageId,
      emoji,
    });
  };

  const handleUploadFile = async (file) => {
    setUploading(true);
    try {
      const res = await uploadChatFilePortal(file);
      return res.data || res;
    } catch (err) {
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await deleteMessagePortal(messageId);
      setMessages((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.filter((m) => m._id !== messageId);
      });
    } catch (err) {
      alert("Failed to delete message");
    }
  };

  const handleCreateConversation = async (payload) => {
    const res = await createConversationPortal(payload);
    const newConv = res.data || res;
    setConversations((prev) => [newConv, ...(Array.isArray(prev) ? prev : [])]);
    setActiveConversation(newConv);
  };

  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
      <div
        className={`${ activeConversation ? "hidden md:block md:w-80" : "w-full md:w-80"} h-full shrink-0 border-r border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900`} > <ChatSidebar conversations={conversations} activeConversation={activeConversation} onSelectConversation={setActiveConversation} onOpenNewChatModal={() => setIsNewChatModalOpen(true)} currentUserId={currentStaffId} onlineUserIds={onlineUserIds} unreadCounts={unreadCounts} loading={loadingConversations} /> </div> {activeConversation ? ( <div className="flex-1 flex flex-col h-full w-full min-w-0">
          <div className="md:hidden flex items-center px-4 py-2 bg-neutral-100 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-800">
            <button
              onClick={() => setActiveConversation(null)}
              className="flex items-center gap-1.5 text-xs font-semibold text-neutral-700 dark:text-neutral-200 hover:text-neutral-900 dark:hover:text-neutral-100 transition"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Messages
            </button>
          </div>

          <MessageList
            messages={messages}
            currentUserId={currentStaffId}
            onDeleteMessage={handleDeleteMessage}
            onOpenGroupInfo={() => setIsGroupInfoModalOpen(true)}
            onToggleReaction={handleToggleReaction}
            activeConversation={activeConversation}
            typingUsers={typingUsers}
            loading={loadingMessages}
          />
          <MessageInput
            onSendMessage={handleSendMessage}
            onUploadFile={handleUploadFile}
            uploading={uploading}
            onTypingStart={handleTypingStart}
            onTypingStop={handleTypingStop}
          />
        </div>
      ) : (
        <div className="hidden md:flex flex-1 items-center justify-center bg-neutral-50 dark:bg-neutral-900 dark:bg-neutral-950 text-neutral-400 dark:text-neutral-500 text-sm">
          Select a conversation or start a new chat.
        </div>
      )}

      <NewChatModal
        isOpen={isNewChatModalOpen}
        onClose={() => setIsNewChatModalOpen(false)}
        availableUsers={availableUsers}
        onCreateConversation={handleCreateConversation}
      />

      <GroupInfoModal
        isOpen={isGroupInfoModalOpen}
        onClose={() => setIsGroupInfoModalOpen(false)}
        conversation={activeConversation}
        availableUsers={availableUsers}
        onAddMember={addGroupMemberPortal}
        onRemoveMember={removeGroupMemberPortal}
        currentUserId={currentStaffId}
      />
    </div>
  );
};

export default ChatPortalPage;